import { NextSeo } from "next-seo";
import Head from "next/head";
import { useRouter } from "next/router";
import { SITE_URL, SITE_NAME_FA, MAIN_TITLE_SITE } from "@utils/constants";
import { SearchAction, organization, organizationOnline } from "@utils/jsonLd";
import { useEffect } from "react";
import { BRAND_ASSETS } from "@utils/logoAssets";
import GoftinoWidget from "../goftino";
import { light } from "../../../../configs/color/background";
import { Meta } from "@layouts/page";
import { useGoftino } from "@shared/components/chat-setting/useGoftino";
import useCheckFrdsGoftino from "@shared/components/chat-setting/useCheckFrdsGoftino";

const FullPage = ({
  meta,
  children,
}: {
  meta: Meta;
  children: React.ReactNode;
}) => {
  const { locale, locales = [], defaultLocale, asPath, pathname } = useRouter();

  const {
    title,
    image,
    baseUrl = SITE_URL,
    path = pathname === "/explore" ? asPath : asPath.split("?")[0],
    keywords,
    description,
    isAvailable,
    category,
    price,
    releaseDate,
    instructor,
    isHideGoftino,
    nofollow,
    noindex,
  } = meta;

  const url = baseUrl + path;
  const imageUrl = image?.startsWith("https://")
    ? image
    : `${SITE_URL}/${image}`;

  const { hideGoftino } = useGoftino();
  const { isFrdsChat, hideChatFrds } = useCheckFrdsGoftino();

  useEffect(() => {
    if (isHideGoftino) {
      hideGoftino();
      hideChatFrds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHideGoftino, isFrdsChat]);

  const isVideos = pathname && pathname.includes("/videos/");
  return (
    <>
      <NextSeo
        noindex={noindex}
        nofollow={nofollow}
        title={title}
        description={
          description ||
          "فرادرس مرجع آموزش‌های ویدئویی و فیلم‌های آموزشی هوش مصنوعی، مهندسی کامپیوتر، برق، مکانیک، عمران، صنایع و سایر رشته‌های مهندسی"
        }
        languageAlternates={(locales || []).map((m) => ({
          hrefLang: m,
          href: m === defaultLocale ? url : `${baseUrl}/${locale}${path}`,
        }))}
        canonical={
          locale === defaultLocale
            ? url.toLowerCase()
            : `${baseUrl}/${locale}${path}`.toLowerCase()
        }
        titleTemplate={`%s`}
        additionalMetaTags={[
          {
            httpEquiv: "content-type",
            content: "text/html; charset=utf-8",
          },
        ]}
        twitter={{
          site: "@faradars",
        }}
        openGraph={{
          type: "website",
          url,
          title,
          description,
          site_name: SITE_NAME_FA,
          locale: "fa_IR",
          images: image
            ? [
                {
                  url: imageUrl,
                  width: 730,
                  height: 410,
                  alt: "Page ",
                },
              ]
            : undefined,
        }}
      />
      <Head>
        {keywords?.length ? <meta name="keywords" content={keywords} /> : ""}
        <meta name="enamad" content="۸۳۲۳۶۳۲" />
        <meta itemProp="name" content={title || MAIN_TITLE_SITE} />

        <meta
          name="twitter:description"
          content={
            description ||
            "فرادرس مرجع آموزش‌های ویدئویی و فیلم‌های آموزشی هوش مصنوعی، مهندسی کامپیوتر، برق، مکانیک، عمران، صنایع و سایر رشته‌های مهندسی"
          }
        />

        <meta name="twitter:title" content={title || MAIN_TITLE_SITE} />
        <meta
          property="og:image:secure_url"
          content={
            image ||
            "https://cdn.faradars.org/theme/display/ilishop/media/og/logo-tak.png"
          }
        />
        <meta
          name="twitter:image"
          content={
            image ||
            "https://cdn.faradars.org/theme/display/ilishop/media/og/logo-tak.png"
          }
        />
        <meta name="twitter:domain" content="faradars.org" />

        {isAvailable && (
          <meta property="faradars:available" content="موجود است." />
        )}
        {category && <meta property="faradars:category" content={category} />}
        {price && <meta property="faradars:price" content={`${price}`} />}
        {releaseDate && (
          <meta property="video:release_date" content={releaseDate} />
        )}
        {instructor && (
          <meta property="faradars:instructor" content={instructor} />
        )}
        <meta
          itemProp="url"
          content={
            locale === defaultLocale ? url : `${baseUrl}/${locale}${path}`
          }
        />
        <meta
          property="og:url"
          content={
            locale === defaultLocale ? url : `${baseUrl}/${locale}${path}`
          }
        />
        <meta
          name="twitter:url"
          content={
            locale === defaultLocale ? url : `${baseUrl}/${locale}${path}`
          }
        />
        <meta name="twitter:card" content="summary" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="HandheldFriendly" content="true" />
        <link
          rel="shortcut icon"
          href={BRAND_ASSETS.brandLogo}
          type="image/x-icon"
        />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#d41114" />
        <meta name="msapplication-TileColor" content="#2b5797" />
        <meta
          name="theme-color"
          content={light?.fds?.brand?.DEFAULT || "#fff"}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(organization()),
          }}
        />
        {!isVideos && (
          <>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(SearchAction()),
              }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(organizationOnline()),
              }}
            />
          </>
        )}
      </Head>
      <GoftinoWidget />
      {children}
    </>
  );
};

export default FullPage;
